<template>
  <WdsAlert v-if="errorBannerMessage" error class="m-b-15">{{ errorBannerMessage }}</WdsAlert>
  <WdsStack direction-column gap-custom="2rem">
    <WdsText v-if="notes.length === 0" class="tw-text-center" data-test="details-no-notes">
      {{ $t('No notes for this contact') }}
    </WdsText>
    <div
      v-for="note in notes"
      :key="note.id"
      class="tw-bg-true-gray-100 tw-rounded-sm tw-px-3 tw-py-3"
    >
      <WdsStack direction-row gap-sm>
        <WdsAvatar xs :initials="note.author.initials ?? ''" :path="note.author.avatarPath ?? ''" />

        <WdsStack direction-column gap-sm class="tw-grow">
          <WdsStack direction-row justify-space-between gap-sm>
            <WdsStack direction-column gap-custom="0" class="tw-grow">
              <WdsText :text="note.author.name" />

              <WdsStack direction-row gap-xs>
                <WdsText tertiary>
                  {{ note.createdAt }}
                  {{ note.hasBeenUpdated ? `(${$t('edited')})` : '' }}
                </WdsText>
              </WdsStack>
            </WdsStack>

            <WdsMenu
              v-if="!viewOnly && props.trustedContact.viewerCanUpdate"
              placement="bottom-end"
            >
              <template #trigger>
                <WdsButtonGhost>
                  <WdsIcon name="overflow-menu-horizontal" :size="16" />
                </WdsButtonGhost>
              </template>

              <WdsMenuOption
                :disabled="loading || !note.viewerCanUpdate"
                @click="showEditForm(note)"
              >
                <WdsIcon :size="16" name="edit" />
                {{ $t('Edit note') }}
              </WdsMenuOption>

              <WdsMenuOption
                theme="DESTRUCTIVE"
                :disabled="loading || !note.viewerCanDelete"
                @click="note.viewerCanDelete ? runDeleteNoteMutation(note.id) : undefined"
              >
                <WdsIcon :size="16" name="trash-can" />
                {{ $t('Delete') }}
              </WdsMenuOption>
            </WdsMenu>
          </WdsStack>

          <WdsText class="tw-break-all" secondary>{{ note.body }}</WdsText>
        </WdsStack>
      </WdsStack>
    </div>
    <WdsButton
      v-if="showAddNotesButton"
      full-width
      secondary
      data-test="details-add-note"
      @click="showAddForm"
    >
      {{ $t('Add note') }}
    </WdsButton>

    <div v-if="formIsVisible">
      <WdsInputArea
        v-model="selectedNoteBody"
        name="note-body"
        :disabled="loading"
        :error="errors['body']"
        :placeholder="$t('Write notes about the worker for internal use...')"
        data-test="details-note-input"
      />
      <WdsStack align-center justify-end direction-row gap-sm class="tw-mt-4">
        <WdsSpinner v-if="loading" data-test="details-note-input-loading" indeterminate />

        <WdsButton tertiary :disabled="loading" @click="closeForm">{{ $t('Cancel') }}</WdsButton>
        <WdsButton
          v-if="selectedNoteId === null"
          primary
          :disabled="loading || !selectedNoteBody"
          data-test="details-save-note"
          @click="saveNote"
        >
          {{ $t('Add note') }}
        </WdsButton>
        <WdsButton v-if="selectedNoteId !== null" primary :disabled="loading" @click="saveNote">
          {{ $t('Save note') }}
        </WdsButton>
      </WdsStack>
    </div>
  </WdsStack>
</template>

<script lang="ts" setup>
import { computed, ref, nextTick, Ref, inject } from 'vue'
import { useQuery } from '@vue/apollo-composable'
import {
  WdsStack,
  WdsAlert,
  WdsAvatar,
  WdsMenu,
  WdsMenuOption,
  WdsIcon,
  WdsText,
  WdsButton,
  WdsButtonGhost,
  WdsInputArea,
  WdsSpinner,
} from '@worksome/wds-components'

import { publicApiCustomFields } from '@/app-bootstrapping/SetUpApollo'
import { deepClone } from '@/utils/tools'
import { FormValidation } from '@/utils/formValidation'
import { TRUSTED_CONTACTS_NOTES_FRAGMENT } from '@/components/WorkerDetailPanel/graphql/fragments'
import {
  TrustedContactNotesView,
  OrganisationTrustedContactNotesView,
} from '@/components/WorkerDetailPanel/graphql/queries'
import { CreateNoteMutation } from '@/components/GraphQL/mutations/Notes/CreateNoteMutation'
import { UpdateNoteMutation } from '@/components/GraphQL/mutations/Notes/UpdateNoteMutation'
import { DeleteNoteMutation } from '@/components/GraphQL/mutations/Notes/DeleteNoteMutation'
import { useFragment } from '@/gql'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import { NOTE_VIEW_FRAGMENT } from '@/components/GraphQL/queries/Notes'
import { useLocaleTimeAgo } from '@/composables/useLocaleTimeAgo'
import { ViewOnlyInterface, viewOnlyKey } from '@/components/WorkerDetailPanel/injectionKeys'
import { TrustedContactViewFragmentFragment } from '@/gql/graphql'

const props = defineProps<{
  trustedContact: TrustedContactViewFragmentFragment
  accountId: string
  forOrganisation?: boolean
}>()

const { viewOnly } = inject<ViewOnlyInterface>(viewOnlyKey, { viewOnly: ref(false) })
const emit = defineEmits(['update-notes'])

const errorBannerMessage = ref(null)
const errors = ref({})

const formIsVisible: Ref<boolean> = ref(false)
const selectedNoteId: Ref<string | null> = ref(null)
const selectedNoteBody: Ref<string> = ref('')

const query = computed(() =>
  !props.forOrganisation ? TrustedContactNotesView : OrganisationTrustedContactNotesView,
)

const variables = computed(() => ({
  trustedContactId: props.trustedContact?.id,
}))

const { result, loading } = useQuery(query, variables, {
  clientId: 'publicApiCustomFields',
})

const notes = computed(() => {
  const tcNotes = useFragment(TRUSTED_CONTACTS_NOTES_FRAGMENT, result.value?.trustedContact)

  return (tcNotes?.notes.data ?? [])
    .map((note) => useFragment(NOTE_VIEW_FRAGMENT, note))
    .map((note) => ({
      ...note,
      createdAt: useLocaleTimeAgo(note.createdAt).value,
      hasBeenUpdated: note.createdAt !== note.updatedAt,
    }))
})

const showAddNotesButton = computed(
  () => !viewOnly.value && !formIsVisible.value && props.trustedContact?.viewerCanUpdate,
)

const resetErrors = () => {
  errors.value = {}
  errorBannerMessage.value = null
}

const closeForm = () => {
  formIsVisible.value = false
  selectedNoteId.value = null
  selectedNoteBody.value = ''
}

const showAddForm = () => {
  formIsVisible.value = true
}

const showEditForm = (note) => {
  formIsVisible.value = false

  nextTick(() => {
    selectedNoteId.value = note.id
    selectedNoteBody.value = note.body
    formIsVisible.value = true
  })
}

const runCreateNoteMutation = () => {
  loading.value = true

  publicApiCustomFields
    .mutate({
      mutation: CreateNoteMutation,
      variables: {
        input: {
          body: selectedNoteBody.value,
          accountId: props.accountId,
          notableId: props.trustedContact?.id,
        },
      },
      update(cache, { data }) {
        if (data) {
          const { createNote } = data

          const newData = deepClone(
            cache.readQuery({
              query: TrustedContactNotesView,
              variables: {
                trustedContactId: props.trustedContact?.id,
              },
            }),
          )

          newData.trustedContact.notes.data.push(createNote)
          newData.trustedContact.notes.paginatorInfo.total += 1

          cache.writeQuery({
            query: TrustedContactNotesView,
            variables: {
              trustedContactId: props.trustedContact?.id,
            },
            data: newData,
          })
        }
      },
    })
    .then(() => {
      resetErrors()

      closeForm()
    })
    .catch((createNoteMutationError) => {
      resetErrors()
      const { graphQLErrors } = createNoteMutationError

      if (FormValidation.containsFormValidations(graphQLErrors)) {
        errors.value = FormValidation.getSingularGraphqlErrors(graphQLErrors, 'input.')
      } else if (FormValidation.containsErrorMessage(graphQLErrors)) {
        errorBannerMessage.value = graphQLErrors[0].message
      } else {
        errorBannerMessage.value = $t('An error occurred')
      }
    })
    .finally(() => {
      loading.value = false
      emit('update-notes')
    })
}

const runEditNoteMutation = () => {
  if (selectedNoteId.value === null) return // Fixes ts check
  loading.value = true

  publicApiCustomFields
    .mutate({
      mutation: UpdateNoteMutation,
      variables: {
        input: {
          id: selectedNoteId.value,
          body: selectedNoteBody.value,
        },
      },
    })
    .then(() => {
      resetErrors()

      closeForm()
    })
    .catch((updateNoteMutationError) => {
      resetErrors()

      const { graphQLErrors } = updateNoteMutationError

      if (FormValidation.containsFormValidations(graphQLErrors)) {
        errors.value = FormValidation.getSingularGraphqlErrors(graphQLErrors, 'input.')
      } else if (FormValidation.containsErrorMessage(graphQLErrors)) {
        errorBannerMessage.value = graphQLErrors[0].message
      } else {
        errorBannerMessage.value = $t('An error occurred')
      }
    })
    .finally(() => {
      loading.value = false
      emit('update-notes')
    })
}

const saveNote = () => {
  if (selectedNoteId.value === null) {
    runCreateNoteMutation()
  } else {
    runEditNoteMutation()
  }
}

function runDeleteNoteMutation(noteId: string) {
  closeForm()

  loading.value = true

  publicApiCustomFields
    .mutate({
      mutation: DeleteNoteMutation,
      variables: {
        input: {
          id: noteId,
        },
      },
      update(cache, { data }) {
        if (data) {
          const { deleteNote } = data
          const newData = deepClone(
            cache.readQuery({
              query: TrustedContactNotesView,
              variables: {
                trustedContactId: props.trustedContact?.id,
              },
            }),
          )

          const index = newData.trustedContact.notes.data.findIndex((n) => n.id === deleteNote.id)

          if (index !== -1) {
            newData.trustedContact.notes.data.splice(index, 1)
            newData.trustedContact.notes.paginatorInfo.total -= 1
            cache.writeQuery({
              query: TrustedContactNotesView,
              variables: {
                trustedContactId: props.trustedContact?.id,
              },
              data: newData,
            })
          }
        }
      },
    })
    .then(() => {
      resetErrors()
    })
    .catch((deleteNoteMutationError) => {
      resetErrors()

      const { graphQLErrors } = deleteNoteMutationError

      if (FormValidation.containsFormValidations(graphQLErrors)) {
        errors.value = FormValidation.getSingularGraphqlErrors(graphQLErrors, 'input.')
      } else if (FormValidation.containsErrorMessage(graphQLErrors)) {
        errorBannerMessage.value = graphQLErrors[0].message
      } else {
        errorBannerMessage.value = $t('An error occurred')
      }
    })
    .finally(() => {
      loading.value = false
      emit('update-notes')
    })
}
</script>
